interface UploadImageOptions {
  guestId: string
  file: File
  onProgress?: (progress: number) => void
}

interface UploadResult {
  id: string
  imageUrl: string
  thumbnailUrl?: string
}

export async function uploadImage({
  guestId,
  file,
  onProgress,
}: UploadImageOptions): Promise<UploadResult> {
  // 1. Direct Upload URLを取得
  const uploadUrlResponse = await fetch('/api/images/direct-upload', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      guestId,
      requireSignedURLs: false,
      metadata: {
        originalName: file.name,
        size: file.size.toString(),
        type: file.type,
      },
    }),
  })

  if (!uploadUrlResponse.ok) {
    const error = await uploadUrlResponse.json()
    throw new Error(error.error || 'Failed to get upload URL')
  }

  const { id, uploadURL } = await uploadUrlResponse.json()

  // 2. Cloudflare Imagesに直接アップロード
  const formData = new FormData()
  formData.append('file', file)

  const xhr = new XMLHttpRequest()
  
  const uploadPromise = new Promise<void>((resolve, reject) => {
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress(progress)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'))
    })

    xhr.open('POST', uploadURL)
    xhr.send(formData)
  })

  await uploadPromise

  // 3. 画像詳細を取得してVariant URLを取得
  const detailsResponse = await fetch(`/api/images/${id}`)
  if (!detailsResponse.ok) {
    throw new Error('Failed to get image details')
  }

  const details = await detailsResponse.json()
  
  // Variant URLを見つける
  const publicUrl = details.variants?.find((v: string) => v.includes('/public')) || details.variants?.[0]
  const thumbnailUrl = details.variants?.find((v: string) => v.includes('/thumbnail'))

  if (!publicUrl) {
    throw new Error('No image URL found')
  }

  // 4. メディアメタデータをKVに保存（media-listで表示されるようにする）
  const mediaId = crypto.randomUUID()
  await fetch('/api/media/commit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      id: mediaId,
      guestId,
      mediaType: 'image',
      resourceId: id, // Cloudflare Images ID
      originalName: file.name,
      metadata: {
        size: file.size.toString(),
        type: file.type,
        uploadedAt: new Date().toISOString()
      }
    })
  })

  return {
    id: mediaId,
    imageUrl: publicUrl,
    thumbnailUrl,
  }
}

export function validateImageFile(file: File): string | null {
  // ファイルサイズチェック（10MB）
  const maxSize = 10 * 1024 * 1024
  if (file.size > maxSize) {
    return 'ファイルサイズは10MB以下にしてください'
  }

  // ファイルタイプチェック
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  if (!allowedTypes.includes(file.type)) {
    return 'JPEG、PNG、GIF、WebP形式の画像のみアップロード可能です'
  }

  return null
}

interface UploadImagesOptions {
  guestId: string
  files: File[]
  onProgress?: (fileIndex: number, progress: number) => void
  onFileComplete?: (fileIndex: number, result: UploadResult | Error) => void
  maxConcurrent?: number
}

interface BatchUploadResult {
  successful: UploadResult[]
  failed: Array<{ file: File; error: Error }>
}

export async function uploadImages({
  guestId,
  files,
  onProgress,
  onFileComplete,
  maxConcurrent = 3,
}: UploadImagesOptions): Promise<BatchUploadResult> {
  const successful: UploadResult[] = []
  const failed: Array<{ file: File; error: Error }> = []

  // 並列度を制限しながらアップロード
  const activeUploads: Promise<void>[] = []

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    const fileIndex = i

    const uploadTask = uploadImage({
      guestId,
      file,
      onProgress: (progress) => onProgress?.(fileIndex, progress),
    })
      .then((result) => {
        successful.push(result)
        onFileComplete?.(fileIndex, result)
      })
      .catch((error) => {
        const err = error instanceof Error ? error : new Error('Upload failed')
        failed.push({ file, error: err })
        onFileComplete?.(fileIndex, err)
      })

    activeUploads.push(uploadTask)

    // 最大並列数に達したら、どれか一つが終わるまで待つ
    if (activeUploads.length >= maxConcurrent) {
      await Promise.race(activeUploads)
      // 完了したタスクを配列から削除（最初に完了したものを削除）
      activeUploads.shift()
    }
  }

  // 残りのアップロードを待つ
  await Promise.all(activeUploads)

  return { successful, failed }
}